import type { ApiClient } from './api';

/**
 * Static imports for locale JSON files, keyed by language code (e.g. fr-FR).
 * Required for Vite/ESM bundlers that do not support dynamic import() with variable paths.
 */
const LOCALE_MODULES: Record<string, () => Promise<{ default: Record<string, unknown> }>> = {
  'fr-FR': () => import('./locales/fr.json'),
  'en-GB': () => import('./locales/en.json'),
  'de-DE': () => import('./locales/de.json'),
  'es-ES': () => import('./locales/es.json'),
  'es-MX': () => import('./locales/es_MX.json'),
  'pt-PT': () => import('./locales/pt.json'),
  'uk-UA': () => import('./locales/uk.json'),
  'ar-DZ': () => import('./locales/ar_DZ.json'),
  'ar-MA': () => import('./locales/ar_MA.json'),
  'tr-TR': () => import('./locales/tr.json'),
  'ro-RO': () => import('./locales/ro.json'),
};

export interface TranslationResult {
  translations: Record<string, unknown>;
  lastUpdated: Date;
}

export interface GetTranslationOptions {
  api: ApiClient;
  useRemote: boolean;
}

/**
 * Load translations for a language: from remote (api) if useRemote, else from shared locales.
 */
export async function getTranslation(
  language: string,
  options: GetTranslationOptions,
): Promise<TranslationResult> {
  if (options.useRemote) {
    return options.api.get<TranslationResult>(`/translations/${language}`, undefined, {
      showErrorToast: false,
    });
  }

  const loadLocale = LOCALE_MODULES[language];
  if (!loadLocale) {
    throw new Error(`Translation file not found for language: ${language}`);
  }

  try {
    const mod = await loadLocale();
    const translations = mod.default as Record<string, unknown>;
    return {
      translations,
      lastUpdated: new Date(),
    };
  } catch (error) {
    console.error(`Failed to load local translation for ${language}:`, error);
    throw new Error(`Translation file not found for language: ${language}`);
  }
}
